import React, { ReactNode, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import AnimateHeight from '@adsk/alloy-react-animate-height';
import { LinkButton } from '@adsk/alloy-react-button';
import {
  useLocale,
  StylableComponent,
  stylePropType,
} from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';

type SeeMoreProps = StylableComponent<HTMLDivElement> & {
  text: ReactNode;
  collapsedStyle?: StylableComponent<HTMLSpanElement>['style'];
};

const defaultCollapsedStyle = {
  display: '-webkit-box',
  overflow: 'hidden',
  '-webkit-box-orient': 'vertical',
  '-webkit-line-clamp': '3',
};

/**
 * SeeMore component. Allows expanding and collapsing long text.
 */
const SeeMore = React.forwardRef<HTMLDivElement, SeeMoreProps>(
  ({ text, style, collapsedStyle, ...props }, ref) => {
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    const textRef = useRef<HTMLSpanElement>(null);
    const collapsedHeightRef = useRef<number>();
    const [hasEllipsis, setHasEllipsis] = useState(false);
    const [collapsing, setCollapsing] = useState(false);
    const [truncate, setTruncate] = useState(true);

    useEffect(() => {
      if (
        textRef.current &&
        textRef.current.clientHeight < textRef.current.scrollHeight
      ) {
        setHasEllipsis(true);
        if (!collapsedHeightRef.current) {
          collapsedHeightRef.current = textRef.current.clientHeight;
        }
      }
    }, [text]);

    return (
      <div ref={ref} css={[style]} {...props}>
        <AnimateHeight
          isOpen={!hasEllipsis || !truncate}
          height={hasEllipsis ? collapsedHeightRef.current : undefined}
          onAnimationComplete={() => {
            setCollapsing(false);
          }}
        >
          <span
            ref={textRef}
            css={[
              theme.typography.bodyMedium,
              truncate && !collapsing && defaultCollapsedStyle,
              truncate && !collapsing && collapsedStyle,
              {
                color: theme.colors.charcoal900,
              },
            ]}
          >
            {text}
          </span>
        </AnimateHeight>
        {hasEllipsis && (
          <LinkButton
            style={{
              alignSelf: 'start',
              marginTop: 4,
            }}
            onClick={() => {
              if (!truncate) {
                setCollapsing(true);
              }
              setTruncate(!truncate);
            }}
          >
            {truncate ? T.translate('SHOW_MORE') : T.translate('SHOW_LESS')}
          </LinkButton>
        )}
      </div>
    );
  }
);

SeeMore.displayName = 'SeeMore';

SeeMore.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Generic activity text to render */
  text: PropTypes.node.isRequired,
  /** Styles for collapsed state */
  collapsedStyle: stylePropType,
};

export default SeeMore;
